import { useState, useMemo } from 'react';
import { useInspections } from '@/hooks/useInspections';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Search, Calendar, User, FileText, Loader2 } from 'lucide-react';
import { NewInspectionModal } from './NewInspectionModal';
import { InspectionDetailModal } from './InspectionDetailModal';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import type { Inspection } from '@/types/crm';
import { useToast } from '@/hooks/use-toast';

interface ProjectInspectionsTabProps {
  projectId: string;
  projectName: string;
  tenantId: string | null;
}

const STATUS_OPTIONS = [
  { value: 'all', label: 'Alle statussen' },
  { value: 'concept', label: 'Concept', color: 'bg-gray-500' },
  { value: 'ingediend', label: 'Ingediend', color: 'bg-blue-500' },
  { value: 'verwerkt', label: 'Verwerkt', color: 'bg-green-500' }
];

export function ProjectInspectionsTab({ projectId, projectName, tenantId }: ProjectInspectionsTabProps) {
  const [isNewModalOpen, setIsNewModalOpen] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState<Inspection | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const { toast } = useToast();
  const { inspections, isLoading, createInspection, updateInspection } = useInspections(projectId);

  // Filter inspections
  const filteredInspections = useMemo(() => {
    if (!inspections) return [];

    return inspections.filter((inspection) => {
      const matchesSearch =
        searchQuery === '' ||
        inspection.type.toLowerCase().includes(searchQuery.toLowerCase()) ||
        inspection.inspector?.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        inspection.notes?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStatus = statusFilter === 'all' || inspection.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [inspections, searchQuery, statusFilter]);

  // Stats
  const stats = useMemo(() => {
    if (!inspections) return { total: 0, concept: 0, ingediend: 0, verwerkt: 0 };

    return {
      total: inspections.length,
      concept: inspections.filter((i) => i.status === 'concept').length,
      ingediend: inspections.filter((i) => i.status === 'ingediend').length,
      verwerkt: inspections.filter((i) => i.status === 'verwerkt').length
    };
  }, [inspections]);

  const handleCreateInspection = async (inspectionData: any) => {
    try {
      await createInspection(inspectionData);
      toast({
        title: 'Inspectie aangemaakt',
        description: 'De inspectie is succesvol aangemaakt'
      });
    } catch (error) {
      toast({
        title: 'Fout',
        description: 'Er is iets misgegaan bij het aanmaken',
        variant: 'destructive'
      });
    }
  };

  const handleStatusUpdate = async (inspectionId: string, newStatus: string) => {
    try {
      await updateInspection({ id: inspectionId, status: newStatus as 'concept' | 'ingediend' | 'verwerkt' });
      toast({
        title: 'Status bijgewerkt',
        description: 'De status is succesvol aangepast'
      });
    } catch (error) {
      toast({
        title: 'Fout',
        description: 'Er is iets misgegaan bij het bijwerken',
        variant: 'destructive'
      });
    }
  };

  const handleNotesUpdate = async (inspectionId: string, notes: string) => {
    try {
      await updateInspection({ id: inspectionId, notes });
      toast({
        title: 'Notities opgeslagen',
        description: 'De notities zijn succesvol bijgewerkt'
      });
    } catch (error) {
      toast({
        title: 'Fout',
        description: 'Er is iets misgegaan bij het opslaan',
        variant: 'destructive'
      });
    }
  };

  const getStatusBadge = (status: string) => {
    const option = STATUS_OPTIONS.find((s) => s.value === status);
    return (
      <Badge className={option?.color || 'bg-gray-500'}>
        {option?.label || status}
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Totaal</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{stats.total}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Concept</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{stats.concept}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Ingediend</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-blue-600">{stats.ingediend}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Verwerkt</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-green-600">{stats.verwerkt}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters & Actions */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Zoek op type, inspecteur of notities..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[200px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button onClick={() => setIsNewModalOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Nieuwe Inspectie
        </Button>
      </div>

      {/* Inspections List */}
      {filteredInspections.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium mb-2">Geen inspecties gevonden</p>
            <p className="text-sm text-muted-foreground mb-4">
              {searchQuery || statusFilter !== 'all'
                ? 'Probeer andere filters'
                : 'Maak je eerste inspectie aan'}
            </p>
            {!searchQuery && statusFilter === 'all' && (
              <Button onClick={() => setIsNewModalOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Nieuwe Inspectie
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredInspections.map((inspection) => (
            <Card
              key={inspection.id}
              className="cursor-pointer hover:bg-accent/50 transition-colors"
              onClick={() => setSelectedInspection(inspection)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold">{inspection.type}</h3>
                      {getStatusBadge(inspection.status)}
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {format(new Date(inspection.date), 'PPP', { locale: nl })}
                      </div>
                      <div className="flex items-center gap-1">
                        <User className="h-4 w-4" />
                        {inspection.inspector?.email || 'Onbekend'}
                      </div>
                    </div>
                    {inspection.notes && (
                      <p className="text-sm text-muted-foreground line-clamp-1">
                        {inspection.notes}
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modals */}
      <NewInspectionModal
        isOpen={isNewModalOpen}
        onClose={() => setIsNewModalOpen(false)}
        projectId={projectId}
        projectName={projectName}
        tenantId={tenantId}
        onCreated={handleCreateInspection}
      />

      <InspectionDetailModal
        isOpen={!!selectedInspection}
        onClose={() => setSelectedInspection(null)}
        inspection={selectedInspection}
        onStatusUpdate={handleStatusUpdate}
        onNotesUpdate={handleNotesUpdate}
      />
    </div>
  );
}
